/**
 * useBranchBookings — MS Bookings appointments for the FSR's branch.
 *
 * Reads the per-branch Bookings business id from local storage (Config tab
 * writes it there). When unconfigured the hook returns []  + a friendly
 * "configure your Bookings business" message via `error`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { fetchBranchBookings, findBranchBookingsId } from '../services/graphBookingsClient';
import type { Appointment } from '../types';

export interface UseBranchBookingsResult {
  appointments: ReadonlyArray<Appointment>;
  loading: boolean;
  error: string | null;
  configured: boolean;
  reload: () => Promise<void>;
}

export function useBranchBookings(): UseBranchBookingsResult {
  const { user, token } = useAuth();
  const { config } = useConfig();
  const [appointments, setAppointments] = useState<ReadonlyArray<Appointment>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const branchCode = user?.activeBranchCode ?? user?.branchCode ?? '';
  const branchName = useMemo(() => {
    return config.branches.find((b) => b.branchCode === branchCode)?.branchName;
  }, [config.branches, branchCode]);

  const bookingsBusinessId = useMemo(
    () => (branchCode ? findBranchBookingsId(branchCode) : undefined),
    [branchCode],
  );
  const configured = Boolean(bookingsBusinessId);

  const load = useCallback(async (): Promise<void> => {
    if (!branchCode) {
      setAppointments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const opts: {
        token?: string | null;
        branchCode: string;
        branchName?: string;
        bookingsBusinessId?: string;
      } = { branchCode };
      if (token !== undefined) opts.token = token;
      if (branchName !== undefined) opts.branchName = branchName;
      if (bookingsBusinessId !== undefined) opts.bookingsBusinessId = bookingsBusinessId;
      const rows = await fetchBranchBookings(opts);
      setAppointments(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, branchCode, branchName, bookingsBusinessId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { appointments, loading, error, configured, reload: load };
}
